/**
 * Chess Engine for Pink Pixel OS
 * Full standard chess rules: legal move generation, castling, en passant,
 * promotion, check/checkmate/stalemate detection, and Minimax AI.
 */

export const INITIAL_BOARD = [
  ['r', 'n', 'b', 'q', 'k', 'b', 'n', 'r'],
  ['p', 'p', 'p', 'p', 'p', 'p', 'p', 'p'],
  [null, null, null, null, null, null, null, null],
  [null, null, null, null, null, null, null, null],
  [null, null, null, null, null, null, null, null],
  [null, null, null, null, null, null, null, null],
  ['P', 'P', 'P', 'P', 'P', 'P', 'P', 'P'],
  ['R', 'N', 'B', 'Q', 'K', 'B', 'N', 'R']
]

export const PIECE_VALUES = {
  p: 100, n: 320, b: 330, r: 500, q: 900, k: 20000,
  P: 100, N: 320, B: 330, R: 500, Q: 900, K: 20000
}

// Piece-Square positional tables for positional AI evaluation
const PST = {
  P: [
    [0,  0,  0,  0,  0,  0,  0,  0],
    [50, 50, 50, 50, 50, 50, 50, 50],
    [10, 10, 20, 30, 30, 20, 10, 10],
    [5,  5, 10, 25, 25, 10,  5,  5],
    [0,  0,  0, 20, 20,  0,  0,  0],
    [5, -5,-10,  0,  0,-10, -5,  5],
    [5, 10, 10,-20,-20, 10, 10,  5],
    [0,  0,  0,  0,  0,  0,  0,  0]
  ],
  N: [
    [-50,-40,-30,-30,-30,-30,-40,-50],
    [-40,-20,  0,  0,  0,  0,-20,-40],
    [-30,  0, 10, 15, 15, 10,  0,-30],
    [-30,  5, 15, 20, 20, 15,  5,-30],
    [-30,  0, 15, 20, 20, 15,  0,-30],
    [-30,  5, 10, 15, 15, 10,  5,-30],
    [-40,-20,  0,  5,  5,  0,-20,-40],
    [-50,-40,-30,-30,-30,-30,-40,-50]
  ],
  B: [
    [-20,-10,-10,-10,-10,-10,-10,-20],
    [-10,  0,  0,  0,  0,  0,  0,-10],
    [-10,  0,  5, 10, 10,  5,  0,-10],
    [-10,  5,  5, 10, 10,  5,  5,-10],
    [-10,  0, 10, 10, 10, 10,  0,-10],
    [-10, 10, 10, 10, 10, 10, 10,-10],
    [-10,  5,  0,  0,  0,  0,  5,-10],
    [-20,-10,-10,-10,-10,-10,-10,-20]
  ],
  R: [
    [0,  0,  0,  0,  0,  0,  0,  0],
    [5, 10, 10, 10, 10, 10, 10,  5],
    [-5,  0,  0,  0,  0,  0,  0, -5],
    [-5,  0,  0,  0,  0,  0,  0, -5],
    [-5,  0,  0,  0,  0,  0,  0, -5],
    [-5,  0,  0,  0,  0,  0,  0, -5],
    [-5,  0,  0,  0,  0,  0,  0, -5],
    [0,  0,  0,  5,  5,  0,  0,  0]
  ],
  Q: [
    [-20,-10,-10, -5, -5,-10,-10,-20],
    [-10,  0,  0,  0,  0,  0,  0,-10],
    [-10,  0,  5,  5,  5,  5,  0,-10],
    [-5,  0,  5,  5,  5,  5,  0, -5],
    [0,  0,  5,  5,  5,  5,  0, -5],
    [-10,  5,  5,  5,  5,  5,  0,-10],
    [-10,  0,  5,  0,  0,  0,  0,-10],
    [-20,-10,-10, -5, -5,-10,-10,-20]
  ],
  K: [
    [-30,-40,-40,-50,-50,-40,-40,-30],
    [-30,-40,-40,-50,-50,-40,-40,-30],
    [-30,-40,-40,-50,-50,-40,-40,-30],
    [-30,-40,-40,-50,-50,-40,-40,-30],
    [-20,-30,-30,-40,-40,-30,-30,-20],
    [-10,-20,-20,-20,-20,-20,-20,-10],
    [20, 20,  0,  0,  0,  0, 20, 20],
    [20, 30, 10,  0,  0, 10, 30, 20]
  ]
}

export function cloneBoard(board) {
  return board.map(row => [...row])
}

export function createInitialState() {
  return {
    board: cloneBoard(INITIAL_BOARD),
    turn: 'w',
    castling: { K: true, Q: true, k: true, q: true },
    enPassant: null,
    halfMoves: 0,
    fullMoves: 1,
    captured: { w: [], b: [] },
    history: [],
    status: 'playing'
  }
}

export function isWhite(piece) {
  return piece && piece === piece.toUpperCase()
}

export function isBlack(piece) {
  return piece && piece === piece.toLowerCase()
}

export function getPieceColor(piece) {
  if (!piece) return null
  return isWhite(piece) ? 'w' : 'b'
}

export function getKingPosition(board, color) {
  const target = color === 'w' ? 'K' : 'k'
  for (let r = 0; r < 8; r++) {
    for (let c = 0; c < 8; c++) {
      if (board[r][c] === target) return { row: r, col: c }
    }
  }
  return null
}

export function isSquareAttacked(board, targetRow, targetCol, attackerColor) {
  const pawnDir = attackerColor === 'w' ? 1 : -1
  const pawnPiece = attackerColor === 'w' ? 'P' : 'p'
  // Attacking pawns
  const pr = targetRow + pawnDir
  if (pr >= 0 && pr < 8) {
    if (targetCol - 1 >= 0 && board[pr][targetCol - 1] === pawnPiece) return true
    if (targetCol + 1 < 8 && board[pr][targetCol + 1] === pawnPiece) return true
  }

  // Knights
  const knightPiece = attackerColor === 'w' ? 'N' : 'n'
  const knightMoves = [
    [-2, -1], [-2, 1], [-1, -2], [-1, 2],
    [1, -2], [1, 2], [2, -1], [2, 1]
  ]
  for (const [dr, dc] of knightMoves) {
    const nr = targetRow + dr
    const nc = targetCol + dc
    if (nr >= 0 && nr < 8 && nc >= 0 && nc < 8 && board[nr][nc] === knightPiece) {
      return true
    }
  }

  // King
  const kingPiece = attackerColor === 'w' ? 'K' : 'k'
  for (let dr = -1; dr <= 1; dr++) {
    for (let dc = -1; dc <= 1; dc++) {
      if (dr === 0 && dc === 0) continue
      const nr = targetRow + dr
      const nc = targetCol + dc
      if (nr >= 0 && nr < 8 && nc >= 0 && nc < 8 && board[nr][nc] === kingPiece) {
        return true
      }
    }
  }

  // Orthogonal (Rook / Queen)
  const rookPiece = attackerColor === 'w' ? 'R' : 'r'
  const queenPiece = attackerColor === 'w' ? 'Q' : 'q'
  const orthoDirs = [[-1, 0], [1, 0], [0, -1], [0, 1]]
  for (const [dr, dc] of orthoDirs) {
    let r = targetRow + dr
    let c = targetCol + dc
    while (r >= 0 && r < 8 && c >= 0 && c < 8) {
      const p = board[r][c]
      if (p) {
        if (p === rookPiece || p === queenPiece) return true
        break
      }
      r += dr
      c += dc
    }
  }

  // Diagonal (Bishop / Queen)
  const bishopPiece = attackerColor === 'w' ? 'B' : 'b'
  const diagDirs = [[-1, -1], [-1, 1], [1, -1], [1, 1]]
  for (const [dr, dc] of diagDirs) {
    let r = targetRow + dr
    let c = targetCol + dc
    while (r >= 0 && r < 8 && c >= 0 && c < 8) {
      const p = board[r][c]
      if (p) {
        if (p === bishopPiece || p === queenPiece) return true
        break
      }
      r += dr
      c += dc
    }
  }

  return false
}

export function isCheck(board, color) {
  const king = getKingPosition(board, color)
  if (!king) return false
  const opponent = color === 'w' ? 'b' : 'w'
  return isSquareAttacked(board, king.row, king.col, opponent)
}

export function generatePseudoMoves(state, color) {
  const { board, castling, enPassant } = state
  const moves = []
  const isW = color === 'w'
  const forward = isW ? -1 : 1
  const startRow = isW ? 6 : 1
  const promoRow = isW ? 0 : 7

  for (let r = 0; r < 8; r++) {
    for (let c = 0; c < 8; c++) {
      const piece = board[r][c]
      if (!piece || getPieceColor(piece) !== color) continue
      const type = piece.toLowerCase()

      // PAWN
      if (type === 'p') {
        const nextR = r + forward
        if (nextR >= 0 && nextR < 8 && !board[nextR][c]) {
          if (nextR === promoRow) {
            ['Q', 'R', 'B', 'N'].forEach(p => moves.push({ from: { r, c }, to: { r: nextR, c }, promo: isW ? p : p.toLowerCase() }))
          } else {
            moves.push({ from: { r, c }, to: { r: nextR, c } })
            const doubleR = r + forward * 2
            if (r === startRow && !board[doubleR][c]) {
              moves.push({ from: { r, c }, to: { r: doubleR, c }, isDoublePawn: true })
            }
          }
        }
        for (const dc of [-1, 1]) {
          const capC = c + dc
          if (capC >= 0 && capC < 8 && nextR >= 0 && nextR < 8) {
            const target = board[nextR][capC]
            if (target && getPieceColor(target) !== color) {
              if (nextR === promoRow) {
                ['Q', 'R', 'B', 'N'].forEach(p => moves.push({ from: { r, c }, to: { r: nextR, c: capC }, promo: isW ? p : p.toLowerCase() }))
              } else {
                moves.push({ from: { r, c }, to: { r: nextR, c: capC } })
              }
            } else if (enPassant && enPassant.row === nextR && enPassant.col === capC) {
              moves.push({ from: { r, c }, to: { r: nextR, c: capC }, isEnPassant: true })
            }
          }
        }
      }

      // KNIGHT
      else if (type === 'n') {
        const deltas = [[-2, -1], [-2, 1], [-1, -2], [-1, 2], [1, -2], [1, 2], [2, -1], [2, 1]]
        for (const [dr, dc] of deltas) {
          const nr = r + dr
          const nc = c + dc
          if (nr >= 0 && nr < 8 && nc >= 0 && nc < 8) {
            const target = board[nr][nc]
            if (!target || getPieceColor(target) !== color) {
              moves.push({ from: { r, c }, to: { r: nr, c: nc } })
            }
          }
        }
      }

      // BISHOP / ROOK / QUEEN
      else if (type === 'b' || type === 'r' || type === 'q') {
        const directions = []
        if (type === 'b' || type === 'q') directions.push([-1, -1], [-1, 1], [1, -1], [1, 1])
        if (type === 'r' || type === 'q') directions.push([-1, 0], [1, 0], [0, -1], [0, 1])
        for (const [dr, dc] of directions) {
          let nr = r + dr
          let nc = c + dc
          while (nr >= 0 && nr < 8 && nc >= 0 && nc < 8) {
            const target = board[nr][nc]
            if (!target) {
              moves.push({ from: { r, c }, to: { r: nr, c: nc } })
            } else {
              if (getPieceColor(target) !== color) {
                moves.push({ from: { r, c }, to: { r: nr, c: nc } })
              }
              break
            }
            nr += dr
            nc += dc
          }
        }
      }

      // KING
      else if (type === 'k') {
        for (let dr = -1; dr <= 1; dr++) {
          for (let dc = -1; dc <= 1; dc++) {
            if (dr === 0 && dc === 0) continue
            const nr = r + dr
            const nc = c + dc
            if (nr >= 0 && nr < 8 && nc >= 0 && nc < 8) {
              const target = board[nr][nc]
              if (!target || getPieceColor(target) !== color) {
                moves.push({ from: { r, c }, to: { r: nr, c: nc } })
              }
            }
          }
        }
        // Castling
        const enemy = isW ? 'b' : 'w'
        if (isW && r === 7 && c === 4) {
          if (castling.K && !board[7][5] && !board[7][6] && board[7][7] === 'R') {
            if (!isSquareAttacked(board, 7, 4, enemy) && !isSquareAttacked(board, 7, 5, enemy) && !isSquareAttacked(board, 7, 6, enemy)) {
              moves.push({ from: { r, c }, to: { r: 7, c: 6 }, isCastle: 'K' })
            }
          }
          if (castling.Q && !board[7][3] && !board[7][2] && !board[7][1] && board[7][0] === 'R') {
            if (!isSquareAttacked(board, 7, 4, enemy) && !isSquareAttacked(board, 7, 3, enemy) && !isSquareAttacked(board, 7, 2, enemy)) {
              moves.push({ from: { r, c }, to: { r: 7, c: 2 }, isCastle: 'Q' })
            }
          }
        } else if (!isW && r === 0 && c === 4) {
          if (castling.k && !board[0][5] && !board[0][6] && board[0][7] === 'r') {
            if (!isSquareAttacked(board, 0, 4, enemy) && !isSquareAttacked(board, 0, 5, enemy) && !isSquareAttacked(board, 0, 6, enemy)) {
              moves.push({ from: { r, c }, to: { r: 0, c: 6 }, isCastle: 'k' })
            }
          }
          if (castling.q && !board[0][3] && !board[0][2] && !board[0][1] && board[0][0] === 'r') {
            if (!isSquareAttacked(board, 0, 4, enemy) && !isSquareAttacked(board, 0, 3, enemy) && !isSquareAttacked(board, 0, 2, enemy)) {
              moves.push({ from: { r, c }, to: { r: 0, c: 2 }, isCastle: 'q' })
            }
          }
        }
      }
    }
  }

  return moves
}

export function makeSimulatedMove(board, move) {
  const next = cloneBoard(board)
  const piece = next[move.from.r][move.from.c]
  next[move.from.r][move.from.c] = null

  if (move.isEnPassant) {
    const enemyPawnRow = move.from.r
    next[enemyPawnRow][move.to.c] = null
  } else if (move.isCastle) {
    if (move.isCastle === 'K') { next[7][7] = null; next[7][5] = 'R'; }
    else if (move.isCastle === 'Q') { next[7][0] = null; next[7][3] = 'R'; }
    else if (move.isCastle === 'k') { next[0][7] = null; next[0][5] = 'r'; }
    else if (move.isCastle === 'q') { next[0][0] = null; next[0][3] = 'r'; }
  }

  next[move.to.r][move.to.c] = move.promo || piece
  return next
}

export function getLegalMoves(state, color) {
  const pseudoMoves = generatePseudoMoves(state, color)
  return pseudoMoves.filter(move => {
    const simulated = makeSimulatedMove(state.board, move)
    return !isCheck(simulated, color)
  })
}

export function executeMove(state, move) {
  const board = cloneBoard(state.board)
  const piece = board[move.from.r][move.from.c]
  let capturedPiece = board[move.to.r][move.to.c]
  const color = getPieceColor(piece)
  const isW = color === 'w'

  board[move.from.r][move.from.c] = null

  if (move.isEnPassant) {
    capturedPiece = isW ? 'p' : 'P'
    board[move.from.r][move.to.c] = null
  } else if (move.isCastle) {
    if (move.isCastle === 'K') { board[7][7] = null; board[7][5] = 'R'; }
    else if (move.isCastle === 'Q') { board[7][0] = null; board[7][3] = 'R'; }
    else if (move.isCastle === 'k') { board[0][7] = null; board[0][5] = 'r'; }
    else if (move.isCastle === 'q') { board[0][0] = null; board[0][3] = 'r'; }
  }

  board[move.to.r][move.to.c] = move.promo || piece

  // Update castling rights
  const nextCastling = { ...state.castling }
  if (piece === 'K') { nextCastling.K = false; nextCastling.Q = false; }
  if (piece === 'k') { nextCastling.k = false; nextCastling.q = false; }
  if (piece === 'R' && move.from.r === 7 && move.from.c === 7) nextCastling.K = false
  if (piece === 'R' && move.from.r === 7 && move.from.c === 0) nextCastling.Q = false
  if (piece === 'r' && move.from.r === 0 && move.from.c === 7) nextCastling.k = false
  if (piece === 'r' && move.from.r === 0 && move.from.c === 0) nextCastling.q = false

  // If a rook is captured, remove castling rights
  if (capturedPiece === 'R' && move.to.r === 7 && move.to.c === 7) nextCastling.K = false
  if (capturedPiece === 'R' && move.to.r === 7 && move.to.c === 0) nextCastling.Q = false
  if (capturedPiece === 'r' && move.to.r === 0 && move.to.c === 7) nextCastling.k = false
  if (capturedPiece === 'r' && move.to.r === 0 && move.to.c === 0) nextCastling.q = false

  // Update en passant
  const nextEnPassant = move.isDoublePawn ? { row: (move.from.r + move.to.r) / 2, col: move.from.c } : null

  // Update captured list
  const nextCaptured = { w: [...state.captured.w], b: [...state.captured.b] }
  if (capturedPiece) {
    if (isW) nextCaptured.w.push(capturedPiece)
    else nextCaptured.b.push(capturedPiece)
  }

  const nextTurn = isW ? 'b' : 'w'
  const checkState = isCheck(board, nextTurn)

  // Standard Algebraic Notation
  const files = ['a', 'b', 'c', 'd', 'e', 'f', 'g', 'h']
  const ranks = ['8', '7', '6', '5', '4', '3', '2', '1']
  let san = ''
  if (move.isCastle === 'K' || move.isCastle === 'k') san = 'O-O'
  else if (move.isCastle === 'Q' || move.isCastle === 'q') san = 'O-O-O'
  else {
    const pType = piece.toUpperCase()
    if (pType !== 'P') san += pType
    if (capturedPiece) {
      if (pType === 'P') san += files[move.from.c]
      san += 'x'
    }
    san += `${files[move.to.c]}${ranks[move.to.r]}`
    if (move.promo) san += `=${move.promo.toUpperCase()}`
  }

  const simulatedState = { board, castling: nextCastling, enPassant: nextEnPassant }
  const nextLegalMoves = getLegalMoves(simulatedState, nextTurn)
  let status = 'playing'
  if (nextLegalMoves.length === 0) {
    if (checkState) {
      status = isW ? 'white_win' : 'black_win'
      san += '#'
    } else {
      status = 'stalemate'
    }
  } else if (checkState) {
    san += '+'
  }

  const historyEntry = {
    san,
    piece,
    from: move.from,
    to: move.to,
    captured: capturedPiece,
    turn: state.turn
  }

  return {
    board,
    turn: nextTurn,
    castling: nextCastling,
    enPassant: nextEnPassant,
    captured: nextCaptured,
    history: [...state.history, historyEntry],
    halfMoves: (piece.toLowerCase() === 'p' || capturedPiece) ? 0 : state.halfMoves + 1,
    fullMoves: isW ? state.fullMoves : state.fullMoves + 1,
    status
  }
}

export function evaluateBoard(board) {
  let score = 0
  for (let r = 0; r < 8; r++) {
    for (let c = 0; c < 8; c++) {
      const p = board[r][c]
      if (!p) continue
      const isW = isWhite(p)
      const type = p.toUpperCase()
      const val = PIECE_VALUES[type] || 0
      const pstVal = PST[type] ? (isW ? PST[type][r][c] : PST[type][7 - r][c]) : 0
      const total = val + pstVal
      score += isW ? total : -total
    }
  }
  return score
}

export function minimax(state, depth, alpha, beta, isMaximizing) {
  const color = isMaximizing ? 'w' : 'b'
  const legal = getLegalMoves(state, color)

  if (legal.length === 0) {
    if (isCheck(state.board, color)) return isMaximizing ? -50000 : 50000
    return 0
  }

  if (depth === 0) {
    return evaluateBoard(state.board)
  }

  if (isMaximizing) {
    let maxEval = -Infinity
    for (const move of legal) {
      const nextState = executeMove(state, move)
      const ev = minimax(nextState, depth - 1, alpha, beta, false)
      maxEval = Math.max(maxEval, ev)
      alpha = Math.max(alpha, ev)
      if (beta <= alpha) break
    }
    return maxEval
  } else {
    let minEval = Infinity
    for (const move of legal) {
      const nextState = executeMove(state, move)
      const ev = minimax(nextState, depth - 1, alpha, beta, true)
      minEval = Math.min(minEval, ev)
      beta = Math.min(beta, ev)
      if (beta <= alpha) break
    }
    return minEval
  }
}

export function getBestMove(state, difficulty = 'medium') {
  const color = state.turn
  const legal = getLegalMoves(state, color)
  if (legal.length === 0) return null

  if (difficulty === 'easy') {
    const captures = legal.filter(m => state.board[m.to.r][m.to.c] || m.isEnPassant)
    if (captures.length > 0 && Math.random() < 0.5) {
      return captures[Math.floor(Math.random() * captures.length)]
    }
    return legal[Math.floor(Math.random() * legal.length)]
  }

  if (difficulty === 'medium') {
    if (Math.random() < 0.2) {
      return legal[Math.floor(Math.random() * legal.length)]
    }
    let best = null
    let bestScore = color === 'w' ? -Infinity : Infinity
    for (const move of legal) {
      const next = executeMove(state, move)
      const score = evaluateBoard(next.board)
      if (color === 'w') {
        if (score > bestScore) { bestScore = score; best = move; }
      } else {
        if (score < bestScore) { bestScore = score; best = move; }
      }
    }
    return best || legal[0]
  }

  // Hard
  const depth = 2
  let best = null
  let bestScore = color === 'w' ? -Infinity : Infinity
  const isMax = color === 'w'

  for (const move of legal) {
    const next = executeMove(state, move)
    const score = minimax(next, depth - 1, -Infinity, Infinity, !isMax)
    if (isMax) {
      if (score > bestScore) { bestScore = score; best = move; }
    } else {
      if (score < bestScore) { bestScore = score; best = move; }
    }
  }

  return best || legal[0]
}
